/**
 * AS PROVAS DE CAPACIDADE, À VISTA (03/11, pedido do Pedro: "quero que fique
 * em evidência as provas de que a empresa tem capacidade para participar
 * daquela licitação… assim que ele abrir a tela", e "coloque os trechos das
 * provas onde são evidenciadas as capacidades de execução").
 *
 * Mora no CABEÇALHO do detalhe, logo depois dos quatro dados — fora das abas:
 * a prévia ao lado da lista e a página própria mostram o mesmo bloco, em
 * qualquer aba aberta. A aba Atestados continua com a lista inteira, linha a
 * linha; aqui vão as três provas mais fortes, cada uma com as DUAS PONTAS
 * copiadas: o que a oportunidade pede e o que o atestado diz.
 *
 * AS PROVAS NÃO VALEM IGUAL, e cada uma diz de onde vem — da mais forte para a
 * mais fraca, que é a ordem da lista:
 *   1. a LEITURA DO EDITAL (a matriz): a exigência citada do edital e o trecho
 *      do atestado que a cobre, conferido por código no texto dele;
 *   2. a INDICAÇÃO DE UMA PESSOA (D11): quem indicou, e a nota;
 *   3. o CRUZAMENTO PELA FICHA (0152): a IA julgou o trabalho, e o código
 *      conferiu os dois trechos palavra a palavra. Vem da medição gravada
 *      (`score.ficha`), com o score saindo dela ou não.
 *
 * PALAVRAS EM COMUM NÃO SÃO PROVA (04/11, o Pedro: "não dá pra fazer indícios
 * por palavras, deve ser pela ficha inteira"). A oportunidade que ainda não foi
 * cruzada pela ficha diz isso, e o botão "Cruzar pela ficha" a mede agora.
 *
 * Só mostra: nada aqui mexe em score, aba ou recomendação. Os dados são os da
 * aba Atestados (mesmas chaves de cache), então abrir a aba depois não custa
 * consulta nenhuma.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ErroApi, type AtestadoProximo, type ExigenciaCruzada,
  type ExplicacaoDaCobertura, type FichaDaOportunidade,
  type LicitacaoDetalhe } from "../api/cliente";
import { SeloNaoAtestado } from "./Selos";
import { Botao, IconeSeta } from "../ds";
import css from "./ProvasDeCapacidade.module.css";

/** As provas que aparecem sem clicar. As outras ficam na aba Atestados. */
const A_VISTA = 3;

type Forca = "edital" | "gestao" | "ficha";

type Prova = {
  codigo: string;
  forca: Forca;
  /** `total`, `parcial`, ou nulo quando a prova não julga cobertura. */
  cobertura: "total" | "parcial" | null;
  /** A ponta da oportunidade: a exigência do edital, ou o trecho do objeto. */
  pede: string | null;
  pedeRotulo: string;
  /** A ponta da oportunidade é o OBJETO inteiro (palavras, gestão): o bloco o
   * mostra uma vez só, em cima, e a prova não o repete. */
  pedeComum?: boolean;
  /** A ponta do atestado: o trecho que prova. */
  prova: string | null;
  provaRotulo: string;
  /** O que falta (o par em parte), o porquê da IA, a nota da gestão. */
  apoio: string | null;
  /** O que o par cobre e por quê (04/11), quando o servidor a montou. */
  explicacao?: ExplicacaoDaCobertura | null;
  /** Quantas exigências do edital este atestado cobre (só a leitura). */
  exigencias?: { total: number; parcial: number };
  natureza?: string | null;
};

const ORDEM: Record<Forca, number> = { edital: 0, gestao: 1, ficha: 2 };

/** O trecho com as palavras que casaram entre ⟦ ⟧, como o servidor marca. */
export function Trecho({ texto }: { texto: string }) {
  const partes = texto.split(/(⟦[^⟧]*⟧)/g).filter(Boolean);
  return (
    <q className={css.trecho}>
      {partes.map((p, i) =>
        p.startsWith("⟦") ? <mark key={i}>{p.slice(1, -1)}</mark> : <span key={i}>{p}</span>,
      )}
    </q>
  );
}

/**
 * O QUE O TRECHO COBRE E POR QUÊ (pedido do Pedro, 04/11: "o que você levou em
 * conta para dizer que cobre por inteiro ou em parte, deixando exatamente
 * explícito o que cobre, não deixando somente o trecho lá"). Montada pelo
 * servidor com o que o par gravou (`matriz.explica_par`,
 * `cruzamento_ficha.explica_par`), sem IA nova: o que cobre, o que não cobre, e
 * o que foi levado em conta — a regra daquele tipo de exigência e o que o código
 * conferiu. Tudo à vista, sem clicar: é a prova, e não um detalhe.
 */
export function Explicacao({ x }: { x: ExplicacaoDaCobertura }) {
  return (
    <div className={css.explica}>
      <p><b>O que cobre.</b> {x.cobre}</p>
      {x.falta ? <p className={css.explicaFalta}><b>O que não cobre.</b> {x.falta}</p> : null}
      <p className={css.explicaConta}>
        <b>Levado em conta.</b> {[x.criterio, ...(x.conferido ?? [])].join(" ")}
      </p>
    </div>
  );
}

const ROTULO_FORCA: Record<Forca, string> = {
  edital: "Pelo edital lido",
  gestao: "Indicado pela gestão",
  ficha: "Pela ficha do atestado",
};

function rotuloCobertura(p: Prova): { texto: string; tom: "ok" | "atencao" | "neutro" } {
  if (p.forca === "gestao") return { texto: "Comprova, pela gestão", tom: "ok" };
  if (p.cobertura === "total") return { texto: "Cobre por inteiro", tom: "ok" };
  return { texto: "Cobre em parte", tom: "atencao" };
}

/** Das exigências que o atestado cobre, a que mais pesa: por inteiro antes de
 * em parte, e o objeto e a qualificação antes do resto (são as duas perguntas
 * da comissão). */
function melhorDaLeitura(exs: { e: ExigenciaCruzada; p: ExigenciaCruzada["atestados"][number] }[]) {
  const peso = (t: string) => (t === "objeto" ? 0 : t === "qualificacao_tecnica" ? 1 : 2);
  return [...exs].sort((a, b) =>
    (a.p.cobertura === "total" ? 0 : 1) - (b.p.cobertura === "total" ? 0 : 1)
    || peso(a.e.tipo) - peso(b.e.tipo) || a.e.n - b.e.n)[0]!;
}

function montaProvas(
  atestados: AtestadoProximo[],
  ficha: FichaDaOportunidade | null | undefined,
  exigencias: ExigenciaCruzada[],
): Prova[] {
  const provas = new Map<string, Prova>();
  const guarda = (p: Prova) => {
    const ja = provas.get(p.codigo);
    if (!ja || ORDEM[p.forca] < ORDEM[ja.forca]) provas.set(p.codigo, p);
  };

  // 1 · a leitura do edital: cada atestado com a exigência que mais pesa.
  const daLeitura = new Map<string, { e: ExigenciaCruzada; p: ExigenciaCruzada["atestados"][number] }[]>();
  for (const e of exigencias) {
    for (const p of e.atestados) {
      daLeitura.set(p.atestado, [...(daLeitura.get(p.atestado) ?? []), { e, p }]);
    }
  }
  for (const [codigo, exs] of daLeitura) {
    const m = melhorDaLeitura(exs);
    guarda({
      codigo,
      forca: "edital",
      cobertura: m.p.cobertura,
      pede: m.e.citacao,
      pedeRotulo: `Responde à exigência ${m.e.n} do edital`,
      prova: m.p.prova ?? null,
      provaRotulo: "trecho conferido no atestado",
      apoio: m.p.cobertura === "parcial" && m.p.falta ? `Falta: ${m.p.falta}` : m.p.rebaixado ?? null,
      explicacao: m.p.explicacao,
      exigencias: {
        total: exs.filter((x) => x.p.cobertura === "total").length,
        parcial: exs.filter((x) => x.p.cobertura === "parcial").length,
      },
    });
  }

  for (const a of atestados) {
    if (a.declarado) {
      guarda({
        codigo: a.codigo,
        forca: "gestao",
        cobertura: null,
        pede: a.trecho_objeto ?? null,
        pedeComum: true,
        pedeRotulo: "A oportunidade contrata",
        prova: a.trecho_documento ?? null,
        provaRotulo: a.trecho_documento_fonte === "descricao"
          ? "trecho da descrição do atestado no acervo" : "trecho do documento do atestado",
        apoio: [a.declarado_por ? `Indicado por ${a.declarado_por}` : "Indicado pela gestão",
          a.declarado_nota].filter(Boolean).join(": "),
        natureza: a.natureza,
      });
    }
  }
  // 3 · A FICHA, da medição gravada — com o score saindo dela ou não.
  for (const f of ficha?.provas ?? []) {
    if (!f.cobertura) continue;
    guarda({
      codigo: f.codigo,
      forca: "ficha",
      cobertura: f.cobertura,
      pede: f.trecho_oportunidade ?? null,
      pedeRotulo: "Responde ao trecho da oportunidade",
      prova: f.trecho_atestado ?? null,
      provaRotulo: "trecho da ficha do atestado, conferido",
      apoio: f.porque ?? null,
      explicacao: f.explicacao,
      natureza: f.natureza,
    });
  }
  // A natureza vem do score também para quem só a leitura trouxe.
  const naturezas = new Map(atestados.map((a) => [a.codigo, a.natureza]));
  const lista = [...provas.values()].map((p) => ({ ...p, natureza: p.natureza ?? naturezas.get(p.codigo) }));
  const ordemDoScore = new Map(atestados.map((a, i) => [a.codigo, i]));
  return lista.sort((a, b) =>
    ORDEM[a.forca] - ORDEM[b.forca]
    || (a.cobertura === "total" ? 0 : 1) - (b.cobertura === "total" ? 0 : 1)
    || ((b.exigencias?.total ?? 0) - (a.exigencias?.total ?? 0))
    || (ordemDoScore.get(a.codigo) ?? 99) - (ordemDoScore.get(b.codigo) ?? 99));
}

/** A frase de cima: o que as provas, juntas, dizem — e com que força. */
function veredito(provas: Prova[], exigencias: ExigenciaCruzada[]): { texto: string; tom: "ok" | "atencao" | "neutro" } {
  const n = (x: number, um: string, varios: string) => `${x} ${x === 1 ? um : varios}`;
  const leitura = provas.filter((p) => p.forca === "edital");
  if (leitura.length) {
    const cobertas = exigencias.filter((e) => e.atestados.some((p) => p.cobertura === "total")).length;
    const emParte = exigencias.filter((e) =>
      !e.atestados.some((p) => p.cobertura === "total") && e.atestados.length > 0).length;
    return {
      texto: `O edital foi lido e cruzado: ${n(leitura.length, "atestado cobre", "atestados cobrem")} `
        + `${n(cobertas, "exigência por inteiro", "exigências por inteiro")}`
        + (emParte ? ` e ${emParte} em parte` : "") + ".",
      tom: cobertas > 0 ? "ok" : "atencao",
    };
  }
  const gestao = provas.filter((p) => p.forca === "gestao").length;
  const ficha = provas.filter((p) => p.forca === "ficha");
  const total = ficha.filter((p) => p.cobertura === "total").length;
  if (gestao || ficha.length) {
    const partes = [
      gestao ? `${n(gestao, "atestado indicado", "atestados indicados")} pela gestão` : null,
      total ? `${n(total, "atestado mostra", "atestados mostram")} o mesmo trabalho, na mesma área` : null,
      ficha.length - total ? `${n(ficha.length - total, "mostra", "mostram")} trabalho próximo` : null,
    ].filter(Boolean);
    return { texto: partes.join("; ").replace(/^./, (c) => c.toUpperCase()) + ".", tom: gestao || total ? "ok" : "atencao" };
  }
  return { texto: "", tom: "neutro" };
}

/**
 * O PEDIDO DA OPORTUNIDADE, quando é o mesmo em todas as provas: o texto sem as
 * marcas, com as palavras de TODAS marcadas. Nulo quando as provas pedem coisas
 * diferentes — aí cada linha mostra o seu.
 */
function pedidoComum(provas: Prova[]): string | null {
  const com = provas.filter((p) => p.pede);
  if (com.length !== provas.length || !com.length) return null;
  const limpo = (t: string) => t.replace(/[⟦⟧]/g, "").replace(/\s+/g, " ").trim();
  const base = limpo(com[0]!.pede!);
  if (com.some((p) => limpo(p.pede!) !== base)) return null;
  const palavras = new Set(com.flatMap((p) =>
    [...p.pede!.matchAll(/⟦([^⟧]*)⟧/g)].map((m) => m[1]!.toLocaleLowerCase("pt-BR"))));
  if (!palavras.size) return base;
  return base.replace(/[\p{L}\p{N}]+/gu, (w) =>
    palavras.has(w.toLocaleLowerCase("pt-BR")) ? `⟦${w}⟧` : w);
}

/**
 * AS PERGUNTAS DA COMISSÃO, na ordem da conta do score (`score.PERGUNTAS`): o
 * objeto, a qualificação técnica e os serviços do termo de referência. São as
 * exigências que a matriz pergunta aos atestados; as outras (entregável,
 * quantitativo, regra de documento, equipe) ficam na aba Edital.
 */
const GRUPOS: { tipo: string; titulo: string }[] = [
  { tipo: "objeto", titulo: "O objeto" },
  { tipo: "qualificacao_tecnica", titulo: "A qualificação técnica" },
  { tipo: "servico", titulo: "Os serviços" },
];
/** Exigências à vista por grupo; as outras, em "mostrar mais". */
const EXIGENCIAS_A_VISTA = 3;
/** Atestados à vista por exigência (a API já manda os de cobertura maior primeiro). */
const PARES_A_VISTA = 2;

type Quem = { trabalho: string | null; cliente: string | null; uf: string | null; ano: number | null };

function CoberturaDe({ c }: { c: ExigenciaCruzada["cobertura"] }) {
  const [texto, tom] = c === "total" ? ["Coberta por inteiro", "ok"]
    : c === "parcial" ? ["Coberta em parte", "atencao"] : ["Nenhum atestado cobre", "nao"];
  return <span className={`${css.cobertura} ${css[`tom_${tom}`]}`}>{texto}</span>;
}

/** UMA EXIGÊNCIA DO EDITAL, com o que o edital pede à esquerda e, à direita,
 * cada atestado que a cobre e o trecho DELE que prova — conferido no texto do
 * atestado pela matriz. É a resposta a "onde e como". */
function LinhaDaExigencia({ e, quem }: { e: ExigenciaCruzada; quem: (c: string) => Quem }) {
  const vistos = e.atestados.slice(0, PARES_A_VISTA);
  const mais = e.atestados.length - vistos.length + (e.mais_atestados ?? 0);
  return (
    <li className={css.linha}>
      <div className={css.lado}>
        <p className={css.linhaTopo}>
          <span className={css.numero}>Exigência {e.n}</span>
          <CoberturaDe c={e.cobertura} />
        </p>
        <Trecho texto={e.citacao} />
      </div>
      <div className={css.lado}>
        {vistos.length === 0 ? (
          <p className={css.apoio}>{e.porque_nenhum || "Nenhum atestado do acervo cobre esta exigência."}</p>
        ) : (
          vistos.map((p) => {
            const q = quem(p.atestado);
            return (
              <div key={p.atestado} className={css.par}>
                <p className={css.linhaTopo}>
                  <Link to={`/atestados/${p.atestado.replace(/^#/, "")}`} className={css.codigo}
                        title="Abrir a ficha do atestado">{p.atestado}</Link>
                  <span className={css.parTitulo}>{q.trabalho || q.cliente || ""}</span>
                  {p.cobertura === "parcial" ? <span className={`${css.cobertura} ${css.tom_atencao}`}>em parte</span> : null}
                </p>
                {p.prova ? (
                  <blockquote className={css.citacao}><Trecho texto={p.prova} /></blockquote>
                ) : null}
                {p.explicacao ? (
                  <Explicacao x={p.explicacao} />
                ) : (
                  <>
                    {p.cobertura === "parcial" && p.falta ? (
                      <p className={`${css.apoio} ${css.falta}`}>Falta: {p.falta}</p>
                    ) : null}
                    {p.rebaixado ? <p className={css.apoio}>{p.rebaixado}</p> : null}
                  </>
                )}
              </div>
            );
          })
        )}
        {mais > 0 ? (
          <p className={css.apoio}>e mais {mais} {mais === 1 ? "atestado" : "atestados"} — na aba Atestados</p>
        ) : null}
      </div>
    </li>
  );
}

/** PELO EDITAL LIDO: exigência por exigência, nas três perguntas da comissão. */
function PeloEdital({ exigencias, quem }: { exigencias: ExigenciaCruzada[]; quem: (c: string) => Quem }) {
  return (
    <div className={css.grupos}>
      <p className={css.cabecalhoLados} aria-hidden="true">
        <span>O edital exige</span>
        <span>O atestado comprova, com o trecho dele</span>
      </p>
      {GRUPOS.map((g) => {
        const exs = exigencias.filter((e) => e.tipo === g.tipo && e.cobertura != null)
          .sort((a, b) => a.n - b.n);
        if (!exs.length) return null;
        const cobertas = exs.filter((e) => e.cobertura === "total").length;
        const parte = exs.filter((e) => e.cobertura === "parcial").length;
        const vistas = exs.slice(0, EXIGENCIAS_A_VISTA);
        const resto = exs.slice(EXIGENCIAS_A_VISTA);
        return (
          <section key={g.tipo} className={css.grupo}>
            <h4 className={css.grupoTitulo}>
              {g.titulo}
              <span className={css.grupoConta}>
                {cobertas} de {exs.length} por inteiro{parte ? ` · ${parte} em parte` : ""}
              </span>
            </h4>
            <ol className={css.linhas}>
              {vistas.map((e) => <LinhaDaExigencia key={e.n} e={e} quem={quem} />)}
            </ol>
            {resto.length ? (
              <details className={css.mais}>
                <summary>mostrar mais {resto.length}</summary>
                <ol className={css.linhas}>
                  {resto.map((e) => <LinhaDaExigencia key={e.n} e={e} quem={quem} />)}
                </ol>
              </details>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

/** PELA FICHA, PELA GESTÃO OU PELAS PALAVRAS: uma linha por atestado, com o que
 * a oportunidade pede à esquerda e o trecho do atestado à direita. Quando a
 * oportunidade pede a mesma coisa a todos, o texto dela fica UMA vez, à
 * esquerda, ao lado de todos. */
function PorAtestado({ provas, quem }: { provas: Prova[]; quem: (c: string) => Quem }) {
  const comum = pedidoComum(provas);
  return (
    <div className={css.grupos}>
      <p className={css.cabecalhoLados} aria-hidden="true">
        <span>A oportunidade contrata</span>
        <span>O atestado comprova, com o trecho dele</span>
      </p>
      <ol className={css.linhas} style={{ ["--n" as string]: provas.length }}>
        {provas.map((p, i) => {
          const q = quem(p.codigo);
          const c = rotuloCobertura(p);
          const meta = [q.trabalho ? q.cliente : null, q.uf, q.ano].filter(Boolean).join(" · ");
          const esquerda = comum ? (i === 0 ? comum : null) : p.pede;
          return (
            <li key={p.codigo} className={css.linha}>
              {comum && i > 0 ? null : (
                <div className={`${css.lado} ${comum ? css.compartilhado : ""}`}>
                  {esquerda ? <Trecho texto={esquerda} /> : <p className={css.apoio}>—</p>}
                </div>
              )}
              <div className={css.lado}>
                <p className={css.linhaTopo}>
                  <Link to={`/atestados/${p.codigo.replace(/^#/, "")}`} className={css.codigo}
                        title="Abrir a ficha do atestado">{p.codigo}</Link>
                  <span className={css.parTitulo}>{q.trabalho || q.cliente || "Atestado do acervo"}</span>
                  <span className={`${css.cobertura} ${css[`tom_${c.tom}`]}`}>{c.texto}</span>
                </p>
                {meta ? <p className={css.meta}>{meta}</p> : null}
                {p.natureza && p.natureza !== "atestado" ? <SeloNaoAtestado natureza={p.natureza} /> : null}
                {p.prova ? (
                  <blockquote className={css.citacao}><Trecho texto={p.prova} /></blockquote>
                ) : null}
                {/* A explicação já traz o que falta e a frase da IA; o apoio
                    fica para quem não tem (a gestão, as palavras). */}
                {p.explicacao ? <Explicacao x={p.explicacao} />
                  : p.apoio ? <p className={css.apoio}>{p.apoio}</p> : null}
                <p className={css.fonte}>{ROTULO_FORCA[p.forca]} · {p.provaRotulo}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Por quanto tempo a tela espera a medição pedida pelo botão: uma chamada da
 * IA leva de meio minuto a uns três minutos. */
const ESPERA_DA_FICHA_MS = 6 * 60 * 1000;

/**
 * AINDA NÃO CRUZADA PELA FICHA (04/11): a frase, e o botão que a mede agora —
 * a mesma medição da noite, só para ela (`POST .../ficha`), que com o score
 * saindo da ficha refaz o score na hora. Custa uma chamada curta da IA, e o
 * botão diz isso. Enquanto mede, a tela pergunta pela ficha a cada 5 s.
 */
function CruzarPelaFicha({ id, ficha, medindo, aoPedir }: {
  id: string;
  ficha: FichaDaOportunidade;
  medindo: boolean;
  aoPedir: () => void;
}) {
  const pedir = useMutation({ mutationFn: () => api.cruzarPelaFicha(id), onSuccess: aoPedir });
  const erro = pedir.error instanceof Error ? pedir.error.message : null;
  if (!ficha.pode_medir) {
    return (
      <p className={css.apoio}>
        Ela não pode ser cruzada agora: foi reprovada por uma pessoa (a reprovada não passa
        por IA) ou ainda não foi triada.
      </p>
    );
  }
  return (
    <div className={css.cruzar}>
      <Botao variante="secundario" pequeno onClick={() => pedir.mutate()}
             disabled={medindo || pedir.isPending}>
        {medindo || pedir.isPending ? "Cruzando pela ficha…" : "Cruzar pela ficha"}
      </Botao>
      <span className={css.apoio}>
        {medindo
          ? "A IA está comparando o objeto com a ficha de cada atestado da área dele. Leva de meio minuto a uns três minutos."
          : "Uma chamada curta da IA, perto de US$ 0,05 a 0,15: escolhe, pela ficha de cada atestado da área, os que mostram o mesmo trabalho, com os dois trechos conferidos."}
      </span>
      {erro ? <p className={`${css.apoio} ${css.falta}`}>{erro}</p> : null}
    </div>
  );
}

export function ProvasDeCapacidade({ d, aoVerTodas }: {
  d: LicitacaoDetalhe;
  /** Abre a aba Atestados, com a lista inteira. */
  aoVerTodas: () => void;
}) {
  // Desde quando a tela espera a medição pedida pelo botão (nulo: não espera).
  const [esperaDesde, setEsperaDesde] = useState<number | null>(null);
  const score = useQuery({
    queryKey: ["score", d.id], queryFn: () => api.scoreDe(d.id), retry: false,
    refetchInterval: (q) =>
      esperaDesde != null && !q.state.data?.ficha?.medida
        && Date.now() - esperaDesde < ESPERA_DA_FICHA_MS ? 5000 : false,
  });
  const leitura = useQuery({ queryKey: ["leitura", d.id], queryFn: () => api.leituraDe(d.id), retry: false });
  // A FICHA DE CADA ATESTADO, para a prova se apresentar pelo TRABALHO que o
  // Nectar fez (o título da ficha), e não só pelo cliente. É a mesma consulta
  // da aba Atestados e da tela de Atestados: uma vez só para as três.
  const acervo = useQuery({
    queryKey: ["atestados", ""],
    queryFn: () => api.atestados({ busca: undefined, quantidade: 1000 }),
  });

  const exigencias = leitura.data?.exigencias_cruzadas ?? [];
  const atestados = score.data?.atestados ?? [];
  const ficha = score.data?.ficha ?? null;
  const provas = montaProvas(atestados, ficha, exigencias);
  const fichas = new Map((acervo.data?.itens ?? []).map((f) => [f.codigo, f]));

  const quem = (codigo: string): Quem => {
    const a = atestados.find((x) => x.codigo === codigo);
    const p = ficha?.provas.find((x) => x.codigo === codigo);
    const f = fichas.get(codigo);
    return {
      trabalho: f?.titulo || null,
      cliente: a?.entidade || p?.entidade || f?.orgao || null,
      uf: a?.uf ?? p?.uf ?? f?.uf ?? null,
      ano: a?.ano ?? p?.ano ?? f?.ano_catalogo ?? null,
    };
  };

  const carregando = score.isPending || leitura.isPending;
  const semScore = score.error instanceof ErroApi && score.error.status === 404;
  const v = provas.length ? veredito(provas, exigencias) : null;
  const medindo = esperaDesde != null && !ficha?.medida
    && Date.now() - esperaDesde < ESPERA_DA_FICHA_MS;
  // MEDIDA: com o score saindo da ficha, o número e a aba podem ter mudado —
  // a tela relê tudo o que mostra desta oportunidade.
  const cliente = useQueryClient();
  useEffect(() => {
    if (esperaDesde != null && ficha?.medida) {
      setEsperaDesde(null);
      void cliente.invalidateQueries();
    }
  }, [esperaDesde, ficha?.medida, cliente]);
  // Sem a ficha medida, e sem a prova do edital lido: a oportunidade ainda não
  // foi cruzada de verdade, e o botão a mede.
  const semFicha = !!ficha && !ficha.medida && !provas.some((p) => p.forca === "edital");
  // O EDITAL LIDO E CRUZADO manda: com ele, a prova é exigência por exigência.
  const peloEdital = provas.some((p) => p.forca === "edital")
    && exigencias.some((e) => GRUPOS.some((g) => g.tipo === e.tipo) && e.cobertura != null);

  return (
    <section className={css.bloco} aria-labelledby={`provas-${d.id}`}>
      <div className={css.cabeca}>
        <h3 id={`provas-${d.id}`} className={css.titulo}>Provas de capacidade</h3>
        {provas.length > 0 ? (
          <Botao variante="texto" pequeno onClick={aoVerTodas} iconeDepois={<IconeSeta tamanho={13} />}>
            Ver detalhes
          </Botao>
        ) : null}
      </div>
      <p className={css.veredito}>
        {carregando
          ? "Buscando as provas no acervo de atestados…"
          : v
            ? v.texto
            : semScore
              ? "Esta oportunidade ainda não foi cruzada com o acervo de atestados."
              : semFicha
                ? "Esta oportunidade ainda não foi cruzada pela ficha dos atestados. Palavras em comum com o objeto não são prova de capacidade, e por isso não aparecem aqui."
                : "Pela ficha, nenhum atestado do acervo mostra o mesmo trabalho. Sem prova, o sistema não recomenda disputar; um atestado que o cruzamento não achou pode ser indicado na aba Atestados."}
      </p>

      {semFicha && !carregando ? (
        <CruzarPelaFicha id={d.id} ficha={ficha!} medindo={medindo}
                         aoPedir={() => setEsperaDesde(Date.now())} />
      ) : null}

      {peloEdital ? (
        <PeloEdital exigencias={exigencias} quem={quem} />
      ) : provas.length ? (
        <PorAtestado provas={provas.slice(0, A_VISTA)} quem={quem} />
      ) : null}
    </section>
  );
}
